import { OfficeStatus } from "@prisma/client";
import type { GetServerSidePropsContext } from "next";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { z } from "zod";

import { validateEmailAddress } from "@calcom/lib/email";
import prisma from "@calcom/prisma";
import { trpc } from "@calcom/trpc/react";
import type { inferSSRProps } from "@calcom/types/inferSSRProps";
import { Button, TextField } from "@calcom/ui";

import { toBrandedWrapperBrand } from "@lib/patient/toBrandedWrapperBrand";
import { formatPhoneNumber } from "@lib/practice/helper";

import BrandedComponentWrapper from "@components/forms/components/BrandedComponentWrapper";
import { HaveQuestionCard } from "@components/patient/HaveQuestionCard";

type StatementPageProps = inferSSRProps<typeof getServerSideProps>;

const querySchema = z.object({
  user: z.string(),
});

export default function GroupOtpRequest({ group }: StatementPageProps) {
  const router = useRouter();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [patientId, setPatientId] = useState("");
  const [isPhoneRequestSent, setIsPhoneRequestSent] = useState(false);
  const [isEmailRequestSent, setIsEmailRequestSent] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const requestCode = trpc.viewer.payments.sendOtpForGroupOnlinePayment.useMutation({
    onSuccess: ({ mode }) => {
      setErrorMessage("");
      if (mode == "phone") {
        setIsPhoneRequestSent(true);
      } else {
        setIsEmailRequestSent(true);
      }
    },
    onError: (error: any) => {
      setErrorMessage(error?.message);
    },
  });

  const verifyCode = trpc.viewer.payments.verifyOtpForOnlinePayment.useMutation({
    onSuccess: (data) => {
      router.push({
        pathname: `${data.statementLink}`,
        query: {
          uid: data.patientId,
          user: data.officeName,
        },
      });
    },
    onError: (error: any) => {
      setErrorMessage(error?.message);
    },
  });

  const verifyPatientId = trpc.viewer.payments.verifyPatientIdForGroupOnlinePayment.useMutation({
    onSuccess: (data) => {
      router.push({
        pathname: `${data.statementLink}`,
        query: {
          uid: data.patientId,
          user: data.officeName,
        },
      });
    },
    onError: (error: any) => {
      setErrorMessage(error?.message);
    },
  });

  return (
    <BrandedComponentWrapper
      brand={toBrandedWrapperBrand(group, { showBrand: true })}
      securityNote="Payments are secured using TLS & SSL encryption.">
      <div className="grid grow grid-cols-1 border-t border-gray-200 md:grid-cols-7">
        <div className="col-span-4 flex flex-col gap-5 border-gray-200 bg-white py-8 px-1 pb-[60px] text-sm md:border-r md:px-8">
          {group.requestType === "PATIENTID" ? (
            <>
              <div className="">
                <h2 className="mb-2 text-xl font-bold text-gray-800">Look up your statement link.</h2>
                <p className="text-sm leading-6 text-gray-600">
                  Input your patient ID below to access your statement link. Once you enter your patient ID,
                  you&apos;ll see your statement link, which breaks down your ledger and the balance you owe.
                </p>
              </div>
              <div className="flex w-96 gap-2">
                <TextField
                  placeholder="Input your patient ID"
                  containerClassName="grow"
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                />
                <Button
                  disabled={!patientId || patientId.trim().length === 0}
                  className="rounded-lg border bg-transparent  bg-white text-gray-600 hover:bg-white hover:text-gray-600"
                  onClick={() => {
                    verifyPatientId.mutate({
                      groupId: group.id,
                      patientId,
                    });
                  }}>
                  Confirm
                </Button>
              </div>
              {errorMessage && (
                <p className="mt-4 rounded-md bg-red-200 py-2 px-3 text-sm text-red-700">{errorMessage}</p>
              )}
            </>
          ) : (
            <>
              <div className="">
                <h2 className="mb-2 text-xl font-bold text-gray-800">Look up your statement link.</h2>
                <p className="text-sm leading-6 text-gray-600">
                  Input your phone number below to connect to your statement link. Once you enter your phone
                  number, we&apos;ll send you a verification code. You&apos;ll then be prompted to input that
                  code. Once verified, you&apos;ll see your statement link, which breaks down your ledger and
                  the balance you owe.
                </p>
              </div>

              <div className="flex w-96 gap-2">
                <TextField
                  placeholder="Input your phone number"
                  containerClassName="grow"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                  addOnLeading="+1"
                />
                <Button
                  disabled={phoneNumber.length < 13}
                  className="rounded-lg border bg-transparent  bg-white text-gray-600 hover:bg-white hover:text-gray-600"
                  onClick={() => {
                    requestCode.mutate({
                      groupId: group.id,
                      phoneNumber,
                    });
                  }}>
                  {isPhoneRequestSent ? "Resend" : "Request"}
                </Button>
              </div>
              <div className="relative w-96 border-t">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform">
                  <p className="bg-white px-2 text-xs">OR</p>
                </div>
              </div>
              <div className="flex w-96 gap-2">
                <TextField
                  placeholder="Input your email"
                  containerClassName="grow"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Button
                  disabled={!validateEmailAddress(email)}
                  className="rounded-lg border bg-transparent  bg-white text-gray-600 hover:bg-white hover:text-gray-600"
                  onClick={() => {
                    requestCode.mutate({
                      groupId: group.id,
                      email,
                    });
                  }}>
                  {isEmailRequestSent ? "Resend" : "Request"}
                </Button>
              </div>
              {(isPhoneRequestSent || isEmailRequestSent) && (
                <div className="flex w-96 gap-2">
                  <TextField
                    containerClassName="grow"
                    placeholder="Input your Verificication code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                  />
                  <Button
                    disabled={!otp || otp.length !== 6}
                    className="rounded-lg border bg-transparent  bg-white text-gray-600 hover:bg-white hover:text-gray-600"
                    onClick={() => {
                      verifyCode.mutate({
                        email,
                        phoneNumber,
                        otp,
                      });
                    }}>
                    Confirm
                  </Button>
                </div>
              )}

              {errorMessage && (
                <p className="mt-4 rounded-md bg-red-200 py-2 px-3 text-sm text-red-700">{errorMessage}</p>
              )}
            </>
          )}
          {/* <div className="border-b border-gray-200" /> */}
        </div>
        <div className="col-span-3 flex flex-col bg-white px-1 py-8 md:px-8">
          <HaveQuestionCard email={group.email} phone={group.phoneNumber} />
        </div>
      </div>
    </BrandedComponentWrapper>
  );
}

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const { user } = querySchema.parse(context.query);

  const env = process.env.FINIX_ENVIRONMENT;
  const appId = process.env.FINIX_APPLICATION_ID;

  const officeGroup = await prisma.officeGroup.findUnique({
    where: {
      groupname: user,
    },
    include: {
      BillingOffice: {
        where: {
          status: {
            in: [OfficeStatus.ACTIVE, OfficeStatus.PAUSE],
          },
        },
      },
    },
  });

  if (!officeGroup || officeGroup.BillingOffice.length === 0) return { notFound: true };

  return {
    props: {
      appId: appId,
      paymentEnv: env,
      group: {
        id: officeGroup.id,
        logo: officeGroup.logo,
        name: officeGroup.name,
        headerColor: officeGroup.headerColor,
        phoneNumber: officeGroup.phone,
        email: officeGroup.email,
        address: officeGroup.address,
        requestType: officeGroup.requestType || "OTP",
      },
    },
  };
};
